import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useRealtime } from "@/hooks/useRealtime";
import { Bell, Check, Trash2, CheckCheck } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const KIND_STYLES = {
  info: "bg-blue-100 text-blue-700",
  success: "bg-emerald-100 text-emerald-700",
  warning: "bg-orange-100 text-orange-700",
  error: "bg-red-100 text-red-700",
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch (err) {
      console.warn("[NotificationBell] loadCount failed", err?.response?.status || err?.message);
    }
  }, []);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data);
    } catch (err) {
      console.warn("[NotificationBell] loadList failed", err?.response?.status || err?.message);
    }
  }, []);

  useEffect(() => {
    loadCount();
    const interval = setInterval(loadCount, 60000); // safety net
    return () => clearInterval(interval);
  }, [loadCount]);

  useRealtime((type) => {
    if (type === "notification") {
      loadCount();
      if (open) loadList();
    }
  });

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    loadCount();
    loadList();
  };
  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    loadCount();
    loadList();
  };
  const remove = async (id) => {
    await api.delete(`/notifications/${id}`);
    loadCount();
    loadList();
  };

  const onClickItem = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-slate-500" data-testid="notif-bell">
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span
              data-testid="notif-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 overflow-hidden" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="font-display font-semibold text-slate-900">Notifications</div>
            <div className="text-xs text-slate-500">{count} non lue{count > 1 ? "s" : ""}</div>
          </div>
          {count > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} data-testid="notif-mark-all-read" className="text-blue-800 hover:text-blue-900">
              <CheckCheck className="w-4 h-4 mr-1" /> Tout lire
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">Aucune notification</div>
          ) : items.map((n) => (
            <div
              key={n.id}
              className={`px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer ${!n.read ? "bg-blue-50/40" : ""}`}
              onClick={() => onClickItem(n)}
              data-testid={`notif-item-${n.id}`}
            >
              <div className="flex items-start gap-2">
                <Badge variant="outline" className={`shrink-0 text-[10px] ${KIND_STYLES[n.kind] || ""}`}>{n.kind}</Badge>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${n.read ? "text-slate-700" : "font-semibold text-slate-900"}`}>{n.title}</div>
                  {n.message && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</div>}
                  <div className="text-[11px] text-slate-400 mt-1 font-mono">
                    {new Date(n.created_at).toLocaleString("fr-FR")}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} title="Marquer lu" className="text-slate-400 hover:text-blue-800">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => remove(n.id)} title="Supprimer" className="text-slate-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
